import { useEffect, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserPlus, Trash2, Inbox, Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import CopyInviteModal from "@/components/CopyInviteModal";
import LanguagePreference from "@/components/LanguagePreference";

function StudentJoinLinkCard() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  if (!user?.academy_slug) return null;
  const link = `${window.location.origin}/join/${user.academy_slug}`;

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="card p-5 flex items-center gap-5" data-testid="student-join-link-card">
      <div className="p-2 rounded-lg" style={{ background: "#fff" }}>
        <QRCodeSVG value={link} size={72} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[14px] uppercase tracking-tight">Student signup link</div>
        <div className="label-tiny mt-0.5" style={{ textTransform: "none", letterSpacing: 0, fontSize: 11 }}>
          Share this link or QR code with real students (Instagram bio, gym wall, flyers). Only signups through this link land on your roster.
        </div>
        <div className="flex items-center gap-2 mt-2">
          <code className="label-tiny mono truncate" style={{ color: "var(--green)" }} data-testid="student-join-link-text">{link}</code>
          <button onClick={copy} className="btn btn-outline shrink-0" style={{ padding: "4px 8px", fontSize: 11 }} data-testid="student-join-link-copy">
            {copied ? <Check size={11}/> : <Copy size={11}/>} {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const academyName = user?.academy_name || "Your Academy";
  const [owners, setOwners] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("owner");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inviteFor, setInviteFor] = useState(null);

  const load = () => {
    api.get("/admin/owners").then(r => setOwners(r.data));
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/owners", { email, role });
      const added = { email, role, email_sent: data.email_sent, email_detail: data.email_detail };
      setEmail(""); load();
      setInviteFor(added);
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const remove = async (e) => {
    if (!confirm(`Remove ${e} from approved owners?`)) return;
    try { await api.delete(`/admin/owners/${encodeURIComponent(e)}`); load(); }
    catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="page-team">
      <PageHeader section="Settings" title="Team & access"/>
      <div className="p-8 space-y-6">
        <LanguagePreference />
        <StudentJoinLinkCard />

        {/* Invite form */}
        <form onSubmit={add} className="card p-6" data-testid="invite-form">
          <div className="label">Invite a teammate</div>
          <div className="text-[18px] font-bold uppercase tracking-tight mt-1">Grant access to RollOSS</div>
          <div className="flex flex-col md:flex-row gap-3 mt-5">
            <input required type="email" className="input flex-1" placeholder="coach@academy.com" value={email} onChange={e=>setEmail(e.target.value)} data-testid="invite-email"/>
            <select className="input md:w-40" value={role} onChange={e=>setRole(e.target.value)} data-testid="invite-role">
              <option value="owner">Owner</option>
              <option value="coach">Coach</option>
            </select>
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="invite-submit"><UserPlus size={14}/>{busy?"Adding…":"Add to allow-list"}</button>
          </div>
          {error && <div className="label-tiny mt-3" style={{color:"var(--danger)"}} data-testid="invite-error">{error}</div>}
          <div className="label-tiny mt-3">They'll be able to sign in via Google immediately. No invitation email sent yet — share the link manually.</div>
        </form>

        {/* Tabs */}
        <div className="card overflow-hidden" data-testid="owners-table">
          {owners.length === 0 ? (
            <div className="p-10 text-center"><Inbox size={22} style={{color:"var(--text-muted)", margin:"0 auto"}}/><div className="label-tiny mt-3">No approved owners yet. Add the first one above.</div></div>
          ) : (
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--border)"}}>
                  <th className="label-tiny text-left p-4">Email</th>
                  <th className="label-tiny text-left p-4">Role</th>
                  <th className="label-tiny text-left p-4">Added</th>
                  <th className="label-tiny text-left p-4">By</th>
                  <th className="label-tiny text-right p-4">·</th>
                </tr>
              </thead>
              <tbody>
                {owners.map(o => (
                  <tr key={o.email} style={{borderBottom:"1px solid var(--border)"}} data-testid={`owner-row-${o.email}`}>
                    <td className="p-4 font-semibold">{o.email}</td>
                    <td className="p-4"><span className="pill pill-active">{o.role || "owner"}</span></td>
                    <td className="p-4 mono text-[12px]" style={{color:"var(--text-dim)"}}>{(o.added_at || "").slice(0,10)}</td>
                    <td className="p-4 label-tiny">{o.added_by || "—"}</td>
                    <td className="p-4 text-right">
                      <button onClick={()=>remove(o.email)} className="btn btn-ghost" style={{padding:"6px 10px"}} data-testid={`remove-${o.email}`}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
      {inviteFor && <CopyInviteModal email={inviteFor.email} role={inviteFor.role} academyName={academyName} emailSent={inviteFor.email_sent} emailDetail={inviteFor.email_detail} onClose={()=>setInviteFor(null)} />}
      <DangerZone onReset={load} academyName={academyName} />
    </div>
  );
}

function DangerZone({ onReset, academyName }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset_academy");
      setResult(data.deleted);
      setConfirming(false);
      onReset?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 md:px-8 pb-10">
      <div className="card p-6" style={{ borderColor: "rgba(255,80,80,0.3)" }} data-testid="danger-zone">
        <div className="label" style={{ color: "rgb(255,120,120)" }}>DANGER ZONE</div>
        <div className="font-bold text-[16px] mt-2 uppercase tracking-tight">Reset academy data</div>
        <div className="label-tiny mt-1" style={{ color: "var(--text-dim)" }}>
          Wipes all students, classes, announcements, reservations, attendance, and push subscriptions for {academyName}.
          Owners, your account, and leads are preserved. Use this to clear demo data before going live.
        </div>
        {result && (
          <div className="mt-4 p-3 mono text-[12px]" style={{ background: "var(--bg-card)", borderRadius: 8 }} data-testid="reset-result">
            Deleted: {Object.entries(result).map(([k, v]) => `${k}=${v}`).join(" · ")}
          </div>
        )}
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="btn btn-outline mt-4"
            style={{ borderColor: "rgba(255,80,80,0.4)", color: "rgb(255,120,120)" }}
            data-testid="btn-reset-init"
          >
            <Trash2 size={14} /> Reset academy data…
          </button>
        ) : (
          <div className="mt-4 flex items-center gap-3">
            <span className="label-tiny" style={{ color: "rgb(255,120,120)" }}>This cannot be undone.</span>
            <button onClick={doReset} disabled={busy} className="btn btn-primary"
              style={{ background: "rgb(220,60,60)", color: "white" }}
              data-testid="btn-reset-confirm">
              {busy ? "Resetting…" : "Yes, wipe everything"}
            </button>
            <button onClick={() => setConfirming(false)} className="btn btn-outline" data-testid="btn-reset-cancel">
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
